import React from 'react';
import useAppContext from 'flow/hooks/useAppContext';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import Icon from 'flow/components/ui-components/Icon';
import { setActiveMeetingMemberListActiveStatus } from 'flow/actions/meeting-actions';
import {
    selectActiveMeetingMemberListActiveStatus,
    selectViewerIsInternal,
} from 'flow/selectors/meeting-selectors';
import useWaitingParticipants from './MeetingParticipantsList/useWaitingParticipants';

/**
 * Header pill announcing that someone is asking to join (VID-76), and the way
 * in to acting on it — clicking toggles the participants panel, where the
 * verdict buttons live, so the thing that opened it also closes it.
 *
 * Stays visible while that panel is open: it is the standing indicator that the
 * queue is non-empty, not a dismissible prompt. Only its label to the panel
 * changes, never whether it is there.
 *
 * Only internal participants see it, matching the section it opens — a guest
 * has nothing to do here (see selectViewerIsInternal).
 */
const MeetingWaitingRoomPill = () => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const viewerIsInternal = useAppSelector(selectViewerIsInternal);
    const membersListActive = useAppSelector(
        selectActiveMeetingMemberListActiveStatus,
    );
    const waitingParticipants = useWaitingParticipants();

    if (!viewerIsInternal || waitingParticipants.length === 0) {
        return null;
    }

    // One person is named; beyond that a name would have to be picked
    // arbitrarily, so it becomes a count.
    const label =
        waitingParticipants.length === 1
            ? i18next.t('videoconference.waiting_room.person_wants_to_join', {
                  name: waitingParticipants[0].displayName,
              })
            : i18next.t('videoconference.waiting_room.people_want_to_join', {
                  count: waitingParticipants.length,
              });

    return (
        <button
            className="flex cursor-pointer items-center gap-2 rounded-xl border-none bg-white px-4 py-3 text-sm text-gray-800 hover:bg-gray-200"
            onClick={() =>
                dispatch(
                    setActiveMeetingMemberListActiveStatus(!membersListActive),
                )
            }
            data-test-id="waiting-room-pill"
        >
            <Icon iconKey="privacyTipOutlined" />
            <span className="truncate">{label}</span>
        </button>
    );
};

export default MeetingWaitingRoomPill;
